import dayjs from "dayjs";
import { sizer } from "layout/styles/styled/sizer";
import { debounce } from "lodash";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllMarketPrices } from "store/queries/marketPrices";
import styled from "styled-components";
import { IMarketPrice } from "types/marketPrice";

const PageContainer = styled.div`
  padding: ${sizer(4)};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${sizer(3)};
`;

const SearchSection = styled.div`
  display: flex;
  gap: ${sizer(2)};
  margin-bottom: ${sizer(3)};
`;

const PriceCell = styled.span`
  font-weight: 600;
  color: #28a745;
`;

const StyledLink = styled.span`
  color: #2196f3;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export function AllMarketPrices() {
  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState("");
  const [competitorFilter, setCompetitorFilter] = useState("");
  const [cptFilter, setCptFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 25,
    page: 0,
    sortField: null as string | null,
    sortOrder: null as -1 | 1 | null,
  });

  // Debounce the search input
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(globalFilter);
    }, 300);

    handler();
    return () => handler.cancel();
  }, [globalFilter]);

  // Convert sort order for API
  let sortOrder: "asc" | "desc" | undefined;
  if (lazyParams.sortOrder === 1) {
    sortOrder = "asc";
  } else if (lazyParams.sortOrder === -1) {
    sortOrder = "desc";
  }

  const { data: pricesData, isLoading: isLoadingPrices } =
    useGetAllMarketPrices({
      competitorName: competitorFilter || undefined,
      cptCode: cptFilter || undefined,
      search: debouncedSearch || undefined,
      limit: lazyParams.rows,
      offset: lazyParams.first,
      sortField: lazyParams.sortField || undefined,
      sortOrder,
    });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const priceBodyTemplate = (rowData: IMarketPrice) => {
    return <PriceCell>{formatCurrency(rowData.rate)}</PriceCell>;
  };

  const payerBodyTemplate = (rowData: IMarketPrice) => {
    return rowData.payer?.name || "Unknown";
  };

  const providerBodyTemplate = (rowData: IMarketPrice) => {
    return rowData.provider?.name || "Unknown";
  };

  const datasetBodyTemplate = (rowData: IMarketPrice) => {
    if (!rowData.marketPriceDataset) {
      return "Unknown";
    }
    return (
      <StyledLink
        onClick={() =>
          navigate(`/payer-rules/market-prices/${rowData.marketPriceDatasetId}`)
        }
      >
        {rowData.marketPriceDataset.displayName}
      </StyledLink>
    );
  };

  const handleBack = () => {
    navigate(`/payer-rules?tab=4`);
  };

  const onSort = (event: any) => {
    setLazyParams(event);
  };

  const onPage = (event: any) => {
    setLazyParams(event);
  };

  if (isLoadingPrices) {
    return (
      <PageContainer>
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      </PageContainer>
    );
  }

  const title = (
    <HeaderSection>
      <div>
        <h2 style={{ margin: 0 }}>All Market Prices</h2>
        <p style={{ margin: "0.5rem 0 0 0", color: "#6c757d" }}>
          View market prices across all uploaded datasets
        </p>
      </div>
      <Button
        label="Back to Datasets"
        icon="pi pi-arrow-left"
        className="p-button-text"
        onClick={handleBack}
      />
    </HeaderSection>
  );

  return (
    <PageContainer>
      <Card title={title}>
        <SearchSection>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search all fields..."
              style={{ width: "300px" }}
            />
          </span>
          <InputText
            value={competitorFilter}
            onChange={(e) => setCompetitorFilter(e.target.value)}
            placeholder="Filter by competitor..."
          />
          <InputText
            value={cptFilter}
            onChange={(e) => setCptFilter(e.target.value)}
            placeholder="Filter by CPT code..."
          />
        </SearchSection>

        <DataTable
          value={pricesData?.data || []}
          loading={isLoadingPrices}
          lazy
          paginator
          rows={lazyParams.rows}
          totalRecords={pricesData?.total || 0}
          first={lazyParams.first}
          onSort={onSort}
          onPage={onPage}
          sortField={lazyParams.sortField || undefined}
          sortOrder={lazyParams.sortOrder || undefined}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage="No market prices found"
          className="datatable-responsive"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} prices"
          rowHover
          stripedRows
        >
          <Column
            field="competitorName"
            header="Competitor"
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="provider.name"
            header="Provider"
            body={providerBodyTemplate}
            sortable
            style={{ width: "15%" }}
          />
          <Column
            field="payer.name"
            header="Payer"
            body={payerBodyTemplate}
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="cptCode"
            header="CPT Code"
            sortable
            style={{ width: "10%" }}
          />
          <Column
            field="rate"
            header="Rate"
            body={priceBodyTemplate}
            sortable
            style={{ width: "15%" }}
          />
          <Column
            field="marketPriceDataset.displayName"
            header="Dataset"
            body={datasetBodyTemplate}
            sortable
            style={{ width: "20%" }}
          />
        </DataTable>
      </Card>
    </PageContainer>
  );
}

export default AllMarketPrices;
